import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
// @ts-ignore -- aabo-sdk.js is a plain JS UMD bundle, no TypeScript types provided
import AaboManager, { Detection, Process } from './aabo-sdk';
import * as mqtt from 'mqtt';
import { environment } from '../../../environments/environment';
import { X6DetectionType, X6State } from '../../@core/interfaces/device-integration/device-item.model';

/**
 * Wraps the real Aabo-provided AaboManager SDK (v3 -- vendor renamed the
 * exported class from X6Manager, and the file from x6sdk.bundle.js, in
 * this release; Detection/Process are unaffected). Replaces the earlier
 * raw Web Bluetooth service (which only did scan+connect, since we didn't
 * have the vitals protocol) -- this uses the actual vendor SDK for all
 * vitals: oxygen, temperature, glucose, blood pressure, ECG, battery.
 * (Device info is a separate case -- see getDevicesVersion() below and the
 * SDK's own v3 release notes: setDeviceInfoListener() is confirmed
 * unsupported in this build, but we never called it -- getDevicesVersion()
 * is a different mechanism and is unaffected.)
 *
 * SETUP REQUIRED (see integration steps provided separately):
 * 1. aabo-sdk.js must sit in this same folder (src/app/_services/aabo-x6/).
 * 2. aabo-ecg.js (ECG's measurement module, replaces the old
 *    x6engine.bundle.js Web Worker -- v3 runs ECG on the main thread
 *    instead) must be served at the root of the built app, alongside
 *    index.html -- via the assets config in angular.json, not just
 *    dropped in src/assets/. Default load path is `/aabo-ecg.js`, which
 *    is what registerAllListeners()'s setECGListener() call below relies
 *    on implicitly by not passing a second argument.
 *
 * SDK version: v3. Oxygen/SpO2's empty-ArrayBuffer(0) defect (two
 * callbacks racing to consume one raw BLE notification) is fixed
 * upstream as of this release -- see registerAllListeners() below for the
 * accompanying app-side change made when that was still an open issue:
 * a global BLE exception listener that was previously never registered.
 * v3 also adds optional `resultTimeoutMs`/`onFingerDetection` params to
 * setOxygenListener() and `onMood`/`onRespiratoryRate` to setECGListener()
 * -- not yet wired in here, since the existing invalidDataStreak/session
 * tracking below already covers similar ground; worth revisiting if the
 * vendor's own watchdog proves more reliable in practice.
 *
 * MQTT publishing: broker URL comes from environment config instead of
 * being hardcoded to localhost (see environment.ts) -- see connectMqtt()
 * below for why that mattered.
 *
 * Ported from EzovionOPDFrontend (Angular 20) core/services/aabo-x6/
 * x6-manager.service.ts to Angular 9 -- only the environment import path
 * was adjusted to this app's folder depth, and the X6DetectionType/X6State
 * types now live in the shared device-integration model file instead of
 * being declared inline here. Do not "clean up" the vendor-workaround
 * timings/comments below without re-verifying against real hardware;
 * several of them exist to work around documented, hardware-confirmed SDK
 * quirks.
 */

const INITIAL_STATE: X6State = {
  status: 'idle'
};

@Injectable({
  providedIn: 'root'
})
export class X6ManagerService {

  private manager: any = null;

  private listenersRegistered = false;

  private readonly stateSubject = new BehaviorSubject<X6State>(INITIAL_STATE);
  readonly state$: Observable<X6State> = this.stateSubject.asObservable();

  private readonly MQTT_TOPIC = 'aabo/x6/status';
  private mqttClient: mqtt.MqttClient | null = null;
  private mqttReconnectAttempts = 0;
  private static readonly MAX_MQTT_RECONNECT_ATTEMPTS = 5;

  private detectionTimeoutHandle: any = null;
  private static readonly DETECTION_TIMEOUT_MS = 45000;

  // FIX (device works for patient 1, then goes unresponsive for patient 2
  // until a physical power-cycle + reconnect): the device stays fully
  // responsive through a patient's own vital sequence (each step seconds
  // apart), but real-world testing shows the FIRST detection of the NEXT
  // patient -- after the longer idle gap while the clinician reviews/saves
  // and picks the next patient in the queue -- times out with no response
  // at all, and only a hardware power-cycle (fresh BLE pairing) recovers
  // it. This is the classic symptom of a BLE peripheral's own power
  // management putting the radio/MCU into a low-power idle state after a
  // period of no GATT traffic -- the Web/JS SDK has no keep-alive of its
  // own and no way to detect this from the connection status alone (the
  // GATT link itself can still report "connected" while the device's
  // firmware is asleep). Mitigated by periodically reading battery (an
  // already-supported, cheap, harmless detection) while connected and idle
  // -- see startKeepAlive()/stopKeepAlive() below -- to keep the device's
  // radio/MCU from ever going idle long enough to need a power-cycle.
  private keepAliveInterval: any = null;
  private static readonly KEEP_ALIVE_INTERVAL_MS = 20000;

  // The setTimeout that defers the real manager.startDetection() call by
  // waitMs (see startDetection() below) -- FIX: previously discarded via a
  // bare setTimeout(reallyStart, waitMs) call, so nothing could cancel it
  // if the connection dropped during that 0-2000ms window. Stored here so
  // disconnect()/a connection error can cancel it before it fires against a
  // dead connection.
  private pendingStartHandle: any = null;

  private lastStopIssuedAt = 0;
  private static readonly MIN_GAP_AFTER_STOP_MS = 500;

  // The vendor's own Android integration (MainActivity.kt, aabo_x6_app repo)
  // documents a BLE notification-enable race on this exact device/firmware:
  // right after connect(), the device isn't always actually ready to send
  // valid notifications yet. Their fix is a 2000ms margin before trusting
  // the connection (their comment: "speculative workaround for the
  // notification-enable race, no confirmed detection hook exists"). The
  // Web/JS SDK exposes no equivalent notification-enabled event, so this
  // mirrors their empirically-validated delay: don't start the first
  // detection until at least this long after connectDevice() resolved.
  private connectedAt = 0;
  private static readonly MIN_GAP_AFTER_CONNECT_MS = 2000;

  // A real device continuously reports code:3 ("Invalid Data") when its
  // sensor doesn't have a clean signal (e.g. a finger not fully/steadily
  // seated on the Oxygen sensor) -- confirmed in a real test log showing
  // ~15-20 of these per second for the entire session. Without tracking
  // this, the app would just sit through the full DETECTION_TIMEOUT_MS
  // before saying anything useful. If this many arrive within the window
  // while a detection is active, treat it as a bad sensor signal and stop
  // early with a friendly message instead.
  private invalidDataStreak = 0;
  private invalidDataStreakStartedAt = 0;
  private static readonly INVALID_DATA_STREAK_THRESHOLD = 20;
  private static readonly INVALID_DATA_STREAK_WINDOW_MS = 4000;

  // ROOT CAUSE (BP false "Cuff Leak"): the SDK guide states the BP onError
  // payload is raised directly by the device and is NOT normalized to a
  // guaranteed field shape. A single err.type === 'leak' check can catch a
  // transient/spurious abort that the device itself mislabels as a leak
  // (observed far more often over Web Bluetooth's less reliable characteristic
  // delivery than over native mobile Bluetooth, against the identical
  // hardware) and show it to the user as a real cuff fault on the very first
  // occurrence. Retried unconditionally instead (counter kept for logging
  // only, no cap) -- a capped retry was tried first and real-device testing
  // showed the cap gave up before a real result ever arrived.
  private bpLeakRetryCount = 0;

  // FIX (BP "finishes" in only a few seconds with a bogus/clamped reading):
  // the leak/noisy retry above used to call startDetection('BP', true)
  // again with no extra delay, so the only gap before the SDK re-armed the
  // cuff was the generic MIN_GAP_AFTER_STOP_MS (500ms). The cuff needs real
  // time to physically deflate back to atmospheric pressure before the
  // SDK's zero-calibration step can trust its reading -- retrying that fast
  // captures a zero baseline against a cuff that's still significantly
  // pressurized, which corrupts every pressure sample for the rest of that
  // retry (they come out clamped/near-zero) and can even reach the success
  // callback looking like a "valid" result after just a couple of these
  // fast, corrupted retry cycles -- matching the observed symptom exactly.
  // Escalating per attempt, same values validated in the sibling
  // AaboWebApp20 implementation: if 5s wasn't enough on retry 1, retry 2
  // needs at least that much again. Retries themselves stay uncapped (see
  // comment above -- a cap was already tried and found insufficient); only
  // the per-attempt delay escalation is capped, by reusing the last value
  // for any retry beyond the array length.
  private static readonly BP_LEAK_RETRY_DEFLATE_DELAYS_MS = [5000, 7000, 10000];

  // Tracks whether ECG's onFingerdetection has reported contact at least
  // once during the current session -- per the SDK guide, onFingerdetection
  // going false "mid-session" (i.e. after contact was established) means
  // the finger lifted off and the measurement should be aborted. Without
  // this flag, the initial false-before-the-user-places-their-finger state
  // would trigger the same abort logic immediately on every ECG start.
  private ecgFingerContactEstablished = false;

  // Same tracking as ecgFingerContactEstablished above, for Oxygen's v3
  // onFingerDetection callback (note the capital D -- the vendor's own
  // casing differs from ECG's onFingerdetection, confirmed against the v3
  // API guide, not a typo here).
  private oxFingerContactEstablished = false;

  // Counts raw PPG samples for the current Oxygen session. onRawResult
  // previously discarded every sample silently, so a session that never
  // produced a result (like the one in the real test log, ~45s of nothing
  // but "Invalid Data") left zero evidence of whether the sensor was
  // sending usable signal at all. Logging a few samples now gives the next
  // captured log something to actually diagnose the root cause with.
  private oxRawSampleCount = 0;

  // Guards, one per vital, for the officially-documented pattern (SDK guide
  // Troubleshooting section, and mirrored by the Android app's own
  // bpSessionActive / onSpO2End handling): Process.END does not by itself
  // mean a result arrived -- the device can run the full measurement window
  // and simply never get a stable reading (confirmed in a real log: 45s of
  // continuous, valid-looking raw Oxygen samples, the SDK's own
  // Process.END firing partway through with no onResult ever called).
  // Without watching for this, the app just sat idle until the generic
  // DETECTION_TIMEOUT_MS fired with a non-specific message. Set true on
  // Process.START, cleared right before any stopDetection() call (from
  // onResult, onError, or here) so a stop's own echoed Process.END can't
  // re-trigger this and loop.
  private oxSessionActive = false;
  private btSessionActive = false;
  private bgSessionActive = false;
  private bpSessionActive = false;
  private batterySessionActive = false;

  constructor() {
    this.connectMqtt();
  }

  private connectMqtt(): void {
    const brokerUrl = environment.mqttBrokerUrl;

    if (!brokerUrl) {
      console.log('[X6Manager] No MQTT broker configured (environment.mqttBrokerUrl is empty) -- skipping MQTT publishing.');
      return;
    }

    console.log('[X6Manager] Connecting to MQTT broker at', brokerUrl);
    try {
      this.mqttClient = mqtt.connect(brokerUrl, { reconnectPeriod: 3000 });

      this.mqttClient.on('connect', () => {
        this.mqttReconnectAttempts = 0;
        console.log('[X6Manager] MQTT connected');
      });

      this.mqttClient.on('error', (err: any) => {
        console.error('[X6Manager] MQTT error:', err?.message ?? err);
      });

      this.mqttClient.on('close', () => {
        this.mqttReconnectAttempts++;
        if (this.mqttReconnectAttempts >= X6ManagerService.MAX_MQTT_RECONNECT_ATTEMPTS) {
          console.warn(
            `[X6Manager] MQTT broker unreachable after ${this.mqttReconnectAttempts} attempts -- giving up. ` +
            'Vitals still work locally over Bluetooth; only remote status publishing is affected.'
          );
          this.mqttClient?.end(true);
        }
      });
    } catch (e) {
      console.error('[X6Manager] Failed to initialize MQTT client:', e);
    }
  }

  private publishState(state: X6State): void {
    if (!this.mqttClient || !this.mqttClient.connected) return;
    this.mqttClient.publish(this.MQTT_TOPIC, JSON.stringify(state), { qos: 0 });
  }

  private ensureManager(): void {
    if (!this.manager) {
      this.manager = new AaboManager('debug');
    }
    if (!this.listenersRegistered) {
      this.registerAllListeners();
      this.listenersRegistered = true;
    }
  }

  isSupported(): boolean {
    this.ensureManager();
    return this.manager.isBTEnabled();
  }

  async connect(): Promise<void> {
    // FIX: no re-entrancy guard here previously -- a second connect() call
    // (e.g. an impatient double-click while the browser's Bluetooth chooser
    // is still open/slow to respond, more likely on a higher-latency
    // deployment than on localhost) fires a second, concurrent
    // getBletoothDevices() against the same AaboManager instance. The SDK's
    // internal "selected device" state isn't safe against that overlap, so
    // whichever connectDevice() call loses the race finds it unset and
    // throws "No device selected. Call getBletoothDevices() first." as an
    // unhandled rejection (that error originates inside the SDK's own
    // promise chain, outside this method's try/catch). Guarding the same
    // way ScopeManagerService/FetalDopplerManagerService already do closes
    // that window.
    const currentStatus = this.stateSubject.value.status;
    if (currentStatus === 'requesting_device' || currentStatus === 'connecting') {
      return;
    }

    this.ensureManager();

    this.setState({ ...this.stateSubject.value, status: 'requesting_device', errorMessage: undefined });

    try {
      await this.manager.getBletoothDevices((err: any) => {
        console.error('[X6Manager] Device chooser error:', err);
        const isDisconnect = err?.code === 'device_disconnected';

        // Per the SDK guide: this same onError callback fires again, any
        // time after connection, if the device drops off -- and X6SDK's
        // internal state does not survive a disconnect. Reset our own
        // session-tracking state here (detection timeout, streak counters,
        // ECG contact flag) or a stale one could later skip a needed
        // stopDetection() call.
        this.clearDetectionTimeout();
        this.clearPendingStart();
        this.stopKeepAlive();
        this.invalidDataStreak = 0;
        this.invalidDataStreakStartedAt = 0;
        this.ecgFingerContactEstablished = false;
        this.oxFingerContactEstablished = false;
        this.oxSessionActive = false;
        this.btSessionActive = false;
        this.bgSessionActive = false;
        this.bpSessionActive = false;
        this.batterySessionActive = false;

        this.setState({
          ...this.stateSubject.value,
          status: isDisconnect ? 'disconnected' : 'error',
          errorMessage: `${err?.code}: ${err?.message}`,
          activeDetection: undefined,
          activeDetectionError: undefined,
          activeDetectionErrorType: undefined
        });
      });

      this.setState({ ...this.stateSubject.value, status: 'connecting' });
      await this.manager.connectDevice();

      this.connectedAt = Date.now();
      this.setState({ ...this.stateSubject.value, status: 'connected' });
      console.log('[X6Manager] Connected');

      try {
        const version = await this.manager.getDevicesVersion();
        this.setState({ ...this.stateSubject.value, deviceInfo: version });
      } catch (e) {
        console.warn('[X6Manager] getDevicesVersion failed (non-fatal):', e);
      }

      // Battery is no longer a user-facing measurement tile -- it's just
      // shown passively in the status bar, so it needs to fetch itself
      // rather than waiting for a tap that no longer exists. One-shot
      // read right after connect; startDetection's own
      // MIN_GAP_AFTER_CONNECT_MS delay already covers the BLE
      // notification-enable race, no extra wait needed here.
      this.startDetection('BATTERY');
      this.startKeepAlive();
    } catch (e) {
      console.error('[X6Manager] Connect flow failed:', e);
      const err: any = e;
      const isCancelled = err?.name === 'NotFoundError';
      this.setState({
        ...this.stateSubject.value,
        status: isCancelled ? 'idle' : 'error',
        errorMessage: isCancelled ? undefined : String(err?.message ?? err)
      });
    }
  }

  private registerAllListeners(): void {
    // Global BLE exception channel -- previously never registered, so
    // exceptions raised outside a specific detection's onError (e.g. the
    // SDK's own internal cleanup write racing an app-initiated stopDetection,
    // which surfaces as "NetworkError: GATT operation already in progress")
    // went completely unobserved by app code.
    this.manager.setBLEListener((err: any) => {
      const { code, message } = err || {};
      const isBenignGattRace = typeof message === 'string' && message.includes('GATT operation already in progress');
      if (isBenignGattRace) {
        // Known-harmless race between the SDK's internal cleanup write and
        // our stop command; the device keeps transmitting afterward.
        console.warn('[X6Manager] BLE exception (benign, ignored):', code, message);
        return;
      }
      console.error('[X6Manager] BLE exception:', code, message);

      const activeDetection = this.stateSubject.value.activeDetection;

      // FIX: this streak-based early-abort used to apply to Oxygen too,
      // and real hardware testing showed it killing every single Oxygen
      // attempt within 3-4 seconds (20 packets at this window) -- long
      // before any real result had a chance to arrive. Root cause found
      // by reading the vendor's own reference demo (X6JSDemo/src/index.js):
      // their onOxygenError/setBLEListener handlers do NOTHING on code:3
      // "Invalid Data" except log it -- no counting, no early-abort at
      // all -- and their own resultTimeoutMs is explicitly set to 60000
      // (not the SDK's documented 15000 default) with the comment "the
      // known Oxygen result-calculation issue can take up to ~40s before
      // the engine gives up on its own". So a burst of Invalid Data
      // packets early in an Oxygen session is expected, normal noise
      // while their engine is still working, not a signal to give up --
      // our own more-aggressive heuristic was fighting the vendor's own
      // tuning and cutting off measurements that would have succeeded
      // given the time their own engine expects to need. Excluding OX
      // here and relying on resultTimeoutMs (see setOxygenListener call
      // below, now also 60000 to match) as the sole Oxygen watchdog,
      // matching the vendor's own reference behavior exactly. Left
      // unchanged for BP/BT/BG/BATTERY, which have no equivalent
      // documented slow-computation quirk and no SDK-provided watchdog
      // of their own to fall back on.
      if (code === 3 && activeDetection && activeDetection !== 'OX') {
        const now = Date.now();
        if (now - this.invalidDataStreakStartedAt > X6ManagerService.INVALID_DATA_STREAK_WINDOW_MS) {
          this.invalidDataStreakStartedAt = now;
          this.invalidDataStreak = 0;
        }
        this.invalidDataStreak++;

        if (this.invalidDataStreak >= X6ManagerService.INVALID_DATA_STREAK_THRESHOLD) {
          const type = activeDetection;
          console.warn(
            `[X6Manager] ${type} received ${this.invalidDataStreak} invalid-data packets within ` +
            `${X6ManagerService.INVALID_DATA_STREAK_WINDOW_MS}ms -- treating as a bad sensor signal, stopping early`
          );
          this.invalidDataStreak = 0;
          this.clearDetectionTimeout();
          this.stopDetectionInternal(type);

          // ROOT CAUSE: without this reset, the type's own xxSessionActive
          // flag stays stuck true after this early-abort. stopDetectionInternal()
          // only issues the SDK stop command -- it doesn't touch the flag --
          // so when the device's own Process.END arrives shortly after
          // acknowledging that stop command, this type's Process.END handler
          // (guarded only on "xxSessionActive is true") fires a SECOND time
          // and overwrites this message with its own hardware-sounding one
          // (e.g. BP's "Could not get a stable blood pressure reading..."),
          // which reads to the user as an unrelated/contradictory cuff or
          // device fault even though the real cause was a noisy BLE signal.
          this.resetSessionActiveFlag(type);

          this.setState({
            ...this.stateSubject.value,
            activeDetectionError: 'Poor sensor signal. Make sure the sensor is making full, steady contact, then try again.',
            activeDetectionErrorType: 'hardware'
          });
        }
      }
    });

    this.manager.setOxygenListener(
      (spo2: number, hr: number) => {
        console.log('[X6Manager] Oxygen result:', spo2, hr);
        this.clearDetectionTimeout();
        this.oxSessionActive = false;
        this.setState({
          ...this.stateSubject.value,
          oxygen: { spo2, hr },
          activeDetectionError: undefined,
          activeDetectionErrorType: undefined
        });
        this.stopDetectionInternal('OX');
      },
      (red: number, ir: number) => {
        this.oxRawSampleCount++;
        // Log the first few samples of each session, then a periodic
        // heartbeat, instead of every single one (this callback fires
        // ~15-20x/second) or none at all.
        if (this.oxRawSampleCount <= 3 || this.oxRawSampleCount % 20 === 0) {
          console.log(`[X6Manager] Oxygen raw sample #${this.oxRawSampleCount}:`, { red, ir });
        }
      },
      (err: any) => {
        console.error('[X6Manager] Oxygen error:', err);
        this.clearDetectionTimeout();
        this.oxSessionActive = false;
        this.oxFingerContactEstablished = false;
        this.stopDetectionInternal('OX');

        // v3 adds a built-in watchdog (resultTimeoutMs below) for exactly
        // the historical "full session, no onResult" defect our own
        // invalidDataStreak/Process.END handling was already built to
        // catch. CONFIRMED real (not just vendor claim): decoded
        // aabo-sdk.js's obfuscated string table directly -- the literal
        // string "oxygen_result_timeout" is genuinely present in the
        // shipped bundle, so this error code is real, implemented code,
        // not documentation-only. Keeping both layers anyway (this one
        // plus our own invalidDataStreak) as defense-in-depth on a medical
        // reading -- give this specific code the same friendly,
        // hardware-attributed message as our own Process.END fallback
        // below, since it's the same underlying situation.
        const isResultTimeout = err?.code === 'oxygen_result_timeout';
        this.setState({
          ...this.stateSubject.value,
          activeDetectionError: isResultTimeout
            ? 'Could not get a stable SpO2 reading. Keep your finger steady on the sensor and try again.'
            : `SpO2 measurement failed: ${err?.message ?? err}`,
          activeDetectionErrorType: isResultTimeout ? 'hardware' : 'sdk'
        });
      },
      (state: number) => {
        console.log('[X6Manager] Oxygen process state:', state);
        if (state === Process.START) {
          this.oxRawSampleCount = 0;
          this.oxSessionActive = true;
        } else if (state === Process.END && this.oxSessionActive) {
          // Mirrors the Android app's onSpO2End handling: the device ran
          // the full measurement window and never got a stable reading.
          this.oxSessionActive = false;
          this.clearDetectionTimeout();
          this.stopDetectionInternal('OX');
          this.setState({
            ...this.stateSubject.value,
            activeDetectionError: 'Could not get a stable SpO2 reading. Keep your finger steady on the sensor and try again.',
            activeDetectionErrorType: 'hardware'
          });
        }
      },
      // resultTimeoutMs: 60000, NOT the SDK's documented 15000 default.
      // FIX: found by reading the vendor's own reference demo
      // (X6JSDemo/src/index.js) -- they deliberately use 60000 with this
      // exact comment: "the known Oxygen result-calculation issue can
      // take up to ~40s before the engine gives up on its own, so 60s
      // gives real (if slow) results room to arrive". Their documented
      // default of 15000 is too short for their own engine's real-world
      // behavior -- using it (or our previous invalidDataStreak logic,
      // which was far more aggressive still, see setBLEListener above)
      // cuts off a measurement that would have succeeded if given the
      // time the vendor's own engine actually needs.
      60000,
      // onFingerDetection (v3, new): mirrors the existing ECG
      // onFingerdetection handling below -- contact lost mid-session after
      // being established is treated as an abort-worthy hardware event,
      // not silently ignored.
      //
      // NOTE: decoded aabo-sdk.js's string table directly (see project
      // notes) and confirmed the literal string "onFingerDetection"
      // (capital D, as documented here for Oxygen) does not exist
      // anywhere in the bundle -- only "onFingerdetection" (lowercase d,
      // ECG's casing) exists, exactly once. Since this parameter is
      // positional, not property-name-based, that doesn't make this call
      // wrong on our end -- we never reference their property name at
      // all. But it's a real, evidence-backed gap between their v3 docs
      // and their shipped code, flagged to the vendor directly rather
      // than assumed fixed. If Oxygen's finger-detection turns out not to
      // be genuinely wired up on their end, this callback will just never
      // fire -- harmless on our side either way, since oxSessionActive's
      // own Process.END fallback above and invalidDataStreak below still
      // catch the same failure mode independently.
      (touch: boolean) => {
        console.log('[X6Manager] Oxygen finger contact:', touch);
        if (touch) {
          this.oxFingerContactEstablished = true;
          return;
        }
        if (this.oxFingerContactEstablished && this.oxSessionActive) {
          this.oxFingerContactEstablished = false;
          this.oxSessionActive = false;
          this.clearDetectionTimeout();
          this.stopDetectionInternal('OX');
          this.setState({
            ...this.stateSubject.value,
            activeDetectionError: 'Finger contact lost. Keep your finger steady on the sensor and try again.',
            activeDetectionErrorType: 'hardware'
          });
        }
      }
    );

    this.manager.setTemperatureListener(
      (temp: number) => {
        console.log('[X6Manager] Temperature result:', temp);
        this.clearDetectionTimeout();
        this.btSessionActive = false;
        this.setState({
          ...this.stateSubject.value,
          temperature: temp,
          activeDetectionError: undefined,
          activeDetectionErrorType: undefined
        });
        this.stopDetectionInternal('BT');
      },
      (err: any) => {
        console.error('[X6Manager] Temperature error:', err);
        this.clearDetectionTimeout();
        this.btSessionActive = false;
        this.stopDetectionInternal('BT');
        this.setState({
          ...this.stateSubject.value,
          activeDetectionError: `Temperature measurement failed: ${err?.message ?? err}`,
          activeDetectionErrorType: 'sdk'
        });
      },
      (state: number) => {
        console.log('[X6Manager] Temperature process state:', state);
        if (state === Process.START) {
          this.btSessionActive = true;
        } else if (state === Process.END && this.btSessionActive) {
          this.btSessionActive = false;
          this.clearDetectionTimeout();
          this.stopDetectionInternal('BT');
          this.setState({
            ...this.stateSubject.value,
            activeDetectionError: 'Could not get a temperature reading. Make sure the sensor is making good contact and try again.',
            activeDetectionErrorType: 'hardware'
          });
        }
      }
    );

    this.manager.setGlucoseListener(
      (result: number) => {
        console.log('[X6Manager] Glucose result:', result);
        this.clearDetectionTimeout();
        this.bgSessionActive = false;
        this.setState({
          ...this.stateSubject.value,
          glucose: result,
          activeDetectionError: undefined,
          activeDetectionErrorType: undefined
        });
        this.stopDetectionInternal('BG');
      },
      (code: number, state: string) => console.log('[X6Manager] Glucose paper state:', code, state),
      (err: any) => {
        console.error('[X6Manager] Glucose error:', err);
        this.clearDetectionTimeout();
        this.bgSessionActive = false;
        this.stopDetectionInternal('BG');
        this.setState({
          ...this.stateSubject.value,
          activeDetectionError: `Glucose measurement failed: ${err?.message ?? err}`,
          activeDetectionErrorType: 'sdk'
        });
      },
      (state: number) => {
        console.log('[X6Manager] Glucose process state:', state);
        if (state === Process.START) {
          this.bgSessionActive = true;
        } else if (state === Process.END && this.bgSessionActive) {
          this.bgSessionActive = false;
          this.clearDetectionTimeout();
          this.stopDetectionInternal('BG');
          this.setState({
            ...this.stateSubject.value,
            activeDetectionError: 'Could not get a glucose reading. Make sure the test strip and sample are correctly applied and try again.',
            activeDetectionErrorType: 'hardware'
          });
        }
      }
    );

    this.manager.setBloodPressureListener(
      (result: { ps: number; pd: number; hr: number }) => {
        console.log('[AABO-X6] BP result (parsed):', result);
        this.clearDetectionTimeout();
        this.bpSessionActive = false;
        this.setState({
          ...this.stateSubject.value,
          bloodPressure: result,
          activeDetectionError: undefined,
          activeDetectionErrorType: undefined
        });
        this.stopDetectionInternal('BP');
      },
      (err: any) => {
        console.error('[AABO-X6] BP device error code/payload:', err);
        this.clearDetectionTimeout();
        this.bpSessionActive = false;
        this.stopDetectionInternal('BP');

        // FIX: originally capped at BP_LEAK_MAX_RETRIES (3) -- real-device
        // testing showed that wasn't enough headroom and BP kept ending in
        // "Cuff leak detected" with a properly-fitted cuff, i.e. never
        // reaching a real result. Retrying unconditionally instead, matching
        // the reference implementation's own field-tested conclusion for
        // this exact hardware/Web-Bluetooth combination: a leak/noisy error
        // here is overwhelmingly a transient comms artifact, not a real cuff
        // fault, so it's never shown to the user -- only a genuinely
        // different (non-leak) SDK error is surfaced.
        if (this.isLeakError(err)) {
          this.bpLeakRetryCount++;
          const delays = X6ManagerService.BP_LEAK_RETRY_DEFLATE_DELAYS_MS;
          const deflateDelay = delays[this.bpLeakRetryCount - 1] ?? delays[delays.length - 1];
          console.warn(`[AABO-X6] BP leak/noisy error (retry ${this.bpLeakRetryCount}) -- silently retrying after ${deflateDelay}ms cuff-deflate delay, never surfaced to the user`);
          this.startDetection('BP', true, deflateDelay);
          return;
        }

        // Only reached for a genuinely different (non-leak/noisy) SDK error --
        // every leak/noisy error is retried above and never reaches here.
        this.setState({
          ...this.stateSubject.value,
          activeDetectionError: `Blood pressure measurement failed: ${err?.message ?? err}`,
          activeDetectionErrorType: 'sdk'
        });
      },
      (state: number) => {
        console.log('[X6Manager] Blood pressure process state:', state);
        // FIX (real bug, confirmed against AaboSDK's own reference
        // Troubleshooting pattern -- see X6 JS SDK v3's demo README, and
        // matching the working AaboWebApp20 implementation): Process.END is
        // a pure lifecycle signal that a session has finished, nothing more
        // -- it fires on BOTH a successful and a failed measurement, and its
        // ordering relative to the result/error callbacks is not guaranteed
        // (confirmed from a real device log: END fired, then the successful
        // result arrived right after). This branch used to ALSO set a
        // "could not get a stable reading" hardware error here
        // unconditionally, which raced the result callback -- if END
        // happened to fire before the result, the user saw a real, valid BP
        // reading get immediately overwritten by a false failure message
        // (self-corrected only if the result callback happened to fire
        // after; a straight loss if it fired first). Per AaboSDK's own
        // reference implementation, this branch should ONLY release the
        // busy-flag/stop the session -- genuine failures are reported
        // exclusively via the dedicated onError callback above (which
        // already handles the real leak-retry logic), never inferred from
        // END.
        if (state === Process.START) {
          this.bpSessionActive = true;
        } else if (state === Process.END && this.bpSessionActive) {
          this.bpSessionActive = false;
          this.clearDetectionTimeout();
          this.stopDetectionInternal('BP');
        }
      }
    );

    this.manager.setBatteryListener(
      (percent: number) => {
        console.log('[X6Manager] Battery result:', percent);
        this.clearDetectionTimeout();
        this.batterySessionActive = false;
        this.setState({
          ...this.stateSubject.value,
          batteryPercent: percent,
          activeDetectionError: undefined,
          activeDetectionErrorType: undefined
        });
        this.stopDetectionInternal('BATTERY');
      },
      () => console.log('[X6Manager] Battery full'),
      (percent: number) => console.log('[X6Manager] Battery changed:', percent),
      (err: any) => {
        console.error('[X6Manager] Battery error:', err);
        this.clearDetectionTimeout();
        this.batterySessionActive = false;
        this.stopDetectionInternal('BATTERY');
        this.setState({
          ...this.stateSubject.value,
          activeDetectionError: `Battery read failed: ${err?.message ?? err}`,
          activeDetectionErrorType: 'sdk'
        });
      },
      (state: number) => {
        console.log('[X6Manager] Battery process state:', state);
        if (state === Process.START) {
          this.batterySessionActive = true;
        } else if (state === Process.END && this.batterySessionActive) {
          this.batterySessionActive = false;
          this.clearDetectionTimeout();
          this.stopDetectionInternal('BATTERY');
          this.setState({
            ...this.stateSubject.value,
            activeDetectionError: 'Could not read battery level. Try again.',
            activeDetectionErrorType: 'hardware'
          });
        }
      }
    );

    // Rolling waveform buffer for the live ECG trace -- kept as a plain
    // instance field rather than always reading/spreading it out of
    // state, since onFilterResult can fire multiple times per second and
    // spreading the whole X6State on every batch would be wasteful.
    let ecgWaveform: number[] = [];
    const ECG_WAVEFORM_MAX_SAMPLES = 600;

    this.manager.setECGListener({
      onProcessState: (s: number) => console.log('[X6Manager] ECG process state:', s),
      // FIX: this used to only console.error and do nothing else -- every
      // other vital's onError clears the watchdog timeout, stops the
      // device-side session, and surfaces activeDetectionError so the UI
      // can react. Without that, a genuine ECG engine error (e.g. an
      // internal exception distinct from the separately-handled
      // onFingerdetection contact-loss case) left the "Capturing..." UI
      // running for its full fixed duration as if nothing went wrong.
      onError: (err: any) => {
        console.error('[X6Manager] ECG error:', err);
        this.clearDetectionTimeout();
        this.stopDetectionInternal('ECG');
        this.setState({
          ...this.stateSubject.value,
          activeDetectionError: `ECG measurement failed: ${err?.message ?? err}`,
          activeDetectionErrorType: 'sdk'
        });
      },
      onFilterResult: (data: number[], start: number) => {
        // start === 0 marks the first batch of a new session (per the SDK
        // guide) -- reset the rolling buffer here rather than relying
        // solely on Process.START, since that's the documented signal.
        if (start === 0) {
          ecgWaveform = [];
        }
        ecgWaveform = ecgWaveform.concat(data);
        if (ecgWaveform.length > ECG_WAVEFORM_MAX_SAMPLES) {
          ecgWaveform = ecgWaveform.slice(ecgWaveform.length - ECG_WAVEFORM_MAX_SAMPLES);
        }
        this.setState({
          ...this.stateSubject.value,
          ecg: { hr: this.stateSubject.value.ecg?.hr ?? 0, waveform: ecgWaveform }
        });
      },
      onHR: (hr: number) => {
        console.log('[X6Manager] ECG HR:', hr);
        this.setState({
          ...this.stateSubject.value,
          ecg: { hr, waveform: this.stateSubject.value.ecg?.waveform ?? [] }
        });
      },
      onFingerdetection: (touch: boolean) => {
        console.log('[X6Manager] ECG finger contact:', touch);
        if (touch) {
          this.ecgFingerContactEstablished = true;
          return;
        }
        if (this.ecgFingerContactEstablished) {
          this.ecgFingerContactEstablished = false;
          this.clearDetectionTimeout();
          this.stopDetectionInternal('ECG');
          this.setState({
            ...this.stateSubject.value,
            activeDetectionError: 'Finger contact lost. Keep your finger steady on the sensor and try again.',
            activeDetectionErrorType: 'hardware'
          });
        }
      },
    });
  }

  // Scans every field the device's BP onError payload has been observed to
  // use (type/code/name/message/error/reason), plus a JSON-stringify
  // fallback, for "leak" or "noisy" (AaboBloodPressureAbnormalType.noisy is
  // the SDK's other device-reported abnormal-abort reason) -- see the
  // bpLeakRetryCount comment above for why a single err.type check isn't
  // enough.
  private isLeakError(err: any): boolean {
    if (!err) return false;
    const candidates = [err.type, err.code, err.name, err.message, err.error, err.reason];
    if (candidates.some(v => typeof v === 'string' && /leak|noisy/i.test(v))) {
      return true;
    }
    try {
      return /leak|noisy/i.test(JSON.stringify(err));
    } catch {
      return /leak|noisy/i.test(String(err));
    }
  }

  /** Clears the one xxSessionActive flag matching `type` -- see the invalid-data-streak abort path above for why this needs to be explicit rather than left to the type's own Process.END handler. */
  private resetSessionActiveFlag(type: X6DetectionType): void {
    switch (type) {
      case 'OX': this.oxSessionActive = false; break;
      case 'BT': this.btSessionActive = false; break;
      case 'BG': this.bgSessionActive = false; break;
      case 'BP': this.bpSessionActive = false; break;
      case 'BATTERY': this.batterySessionActive = false; break;
    }
  }

  private stopDetectionInternal(type: X6DetectionType): void {
    if (!this.manager) return;
    try {
      console.log('[X6Manager] Auto-stopping finished detection:', type);
      this.manager.stopDetection(Detection[type]);
      this.lastStopIssuedAt = Date.now();
    } catch (e) {
      console.warn('[X6Manager] stopDetectionInternal failed for', type, e);
    }
    // FIX: this used to only issue the SDK stop command -- state.activeDetection
    // itself was never cleared on this (internal, auto-triggered) path, only
    // on the public stopDetection()/disconnect()/error paths. That left it
    // pointing at whatever vital last finished, all the way until the NEXT
    // startDetection() call for a DIFFERENT type reads it and issues a
    // redundant extra stop command before starting -- one more real GATT
    // round-trip this device's already-fragile timing (see the MIN_GAP_*
    // margins elsewhere in this file) can't always tolerate right before a
    // fresh start. Only clears when it still matches `type`: startDetection()
    // itself calls this to stop a DIFFERENT in-flight type before setting
    // activeDetection to the new one a few lines later, and this must not
    // clobber that newer value.
    if (this.stateSubject.value.activeDetection === type) {
      this.setState({ ...this.stateSubject.value, activeDetection: undefined });
    }
  }

  /**
   * Clears any previous reading for a vital before starting a fresh
   * measurement of it. FIX: needed once the guided checkup started
   * running the same 'BP' detection twice in a row (a Pulse Rate step
   * followed by a separate Blood Pressure step, both reusing the same
   * cuff reading) -- without this, the UI's "has a result arrived yet"
   * check would see the *previous* step's still-present bloodPressure
   * value and immediately report the new measurement as already
   * complete, before the device had even started the new reading.
   */
  clearResult(type: X6DetectionType): void {
    const next = { ...this.stateSubject.value };
    switch (type) {
      case 'BP': next.bloodPressure = undefined; break;
      case 'OX': next.oxygen = undefined; break;
      case 'BT': next.temperature = undefined; break;
      case 'BG': next.glucose = undefined; break;
      case 'BATTERY': next.batteryPercent = undefined; break;
      case 'ECG': next.ecg = undefined; break;
    }
    this.setState(next);
  }

  // isInternalRetry: set only by the BP leak/noisy silent-retry path above --
  // marks this call as a continuation of the SAME user-initiated measurement
  // attempt, so bpLeakRetryCount (a per-attempt counter) isn't reset out from
  // under it. A normal caller (guided sequence, "Measure" button) never
  // passes this.
  startDetection(type: X6DetectionType, isInternalRetry = false, extraDelayMs = 0): void {
    if (!this.manager) return;

    // Mirrors the explicit "connectedDeviceAddress == null -> NOT_CONNECTED"
    // guard present in every reading-start function on the Android side --
    // this service had no equivalent, so a stray startDetection() call while
    // not connected would reach the SDK in an undefined state instead of
    // failing predictably.
    if (this.stateSubject.value.status !== 'connected') {
      console.warn(`[X6Manager] startDetection(${type}) ignored -- not connected (status=${this.stateSubject.value.status})`);
      return;
    }

    if (type === 'BP' && !isInternalRetry) {
      this.bpLeakRetryCount = 0;
    }

    // FIX: a different detection (e.g. connect()'s own auto-fired BATTERY
    // read) can genuinely still be in flight when this is called -- the
    // device/SDK can only run one detection at a time. Without this, the
    // unconditional resets below would clear that OTHER detection's
    // watchdog timeout and session-active flag out from under it while
    // it's still genuinely running on the device, and this call would then
    // fire a second, concurrent manager.startDetection() without ever
    // telling the device to stop the first one. Stop it cleanly first.
    const currentActive = this.stateSubject.value.activeDetection;
    if (currentActive && currentActive !== type) {
      console.log(`[X6Manager] Stopping in-flight ${currentActive} detection to start ${type}`);
      this.stopDetectionInternal(currentActive);
    }

    this.clearDetectionTimeout();
    this.clearPendingStart();
    this.invalidDataStreak = 0;
    this.invalidDataStreakStartedAt = 0;
    this.ecgFingerContactEstablished = false;
    this.oxFingerContactEstablished = false;
    this.oxRawSampleCount = 0;
    this.oxSessionActive = false;
    this.btSessionActive = false;
    this.bgSessionActive = false;
    this.bpSessionActive = false;
    this.batterySessionActive = false;
    this.setState({
      ...this.stateSubject.value,
      activeDetection: type,
      activeDetectionError: undefined,
      activeDetectionErrorType: undefined
    });

    const elapsedSinceStop = Date.now() - this.lastStopIssuedAt;
    const elapsedSinceConnect = Date.now() - this.connectedAt;
    const waitMs = Math.max(
      0,
      X6ManagerService.MIN_GAP_AFTER_STOP_MS - elapsedSinceStop,
      X6ManagerService.MIN_GAP_AFTER_CONNECT_MS - elapsedSinceConnect,
      extraDelayMs
    );

    const reallyStart = () => {
      this.pendingStartHandle = null;

      // FIX: this closure can run up to MIN_GAP_AFTER_CONNECT_MS (2000ms)
      // after startDetection() was called -- a real BLE disconnect landing
      // in that window (the exact kind of flakiness MIN_GAP_AFTER_CONNECT_MS
      // itself exists to work around) previously meant this ran anyway
      // against a dead connection. Re-check both that the manager still
      // exists AND that we're still actually connected before touching it.
      if (!this.manager || this.stateSubject.value.status !== 'connected') {
        console.warn(`[X6Manager] Skipping deferred start of ${type} -- no longer connected`);
        return;
      }

      console.log('[X6Manager] Starting detection:', type);

      // FIX: previously uncaught -- if the SDK throws synchronously here
      // (plausible against a connection that dropped mid-delay), the code
      // below that registers the stuck-detection watchdog never ran,
      // leaving activeDetection/capturingType stuck with no timeout and no
      // error ever surfaced to the user.
      try {
        this.manager.startDetection(Detection[type]);
      } catch (e) {
        console.error(`[X6Manager] startDetection(${type}) threw:`, e);
        this.setState({
          ...this.stateSubject.value,
          activeDetectionError: `${type} measurement failed to start: ${(e as any)?.message ?? e}`,
          activeDetectionErrorType: 'sdk'
        });
        return;
      }

      // FIX: Oxygen now has its own vendor-tuned watchdog (resultTimeoutMs
      // = 60000, see setOxygenListener above) precisely because the
      // vendor's own engine can legitimately take up to ~40s to produce a
      // result. Our own generic DETECTION_TIMEOUT_MS (45000) would fire
      // first and cut it off before that watchdog ever gets a chance --
      // the same class of "our own timer fights the vendor's tuning" bug
      // just fixed for invalidDataStreak above. Give OX a generic backstop
      // safely above 60000 instead, so it only ever fires as a true
      // last-resort (e.g. resultTimeoutMs's own onError somehow never
      // arriving), not as the actual cutoff.
      // BP gets its own longer window, same reasoning as OX's 70000 above:
      // a real oscillometric cycle already commonly runs 30-40s on its own,
      // and each individual leak-retry attempt (see
      // BP_LEAK_RETRY_DEFLATE_DELAYS_MS) stacks its deflate delay -- up to
      // 10000ms -- ON TOP of that same full cycle, since each retry gets
      // its own fresh timeout window here (clearDetectionTimeout() runs at
      // the top of startDetection() on every call, including retries). At
      // the generic 45000ms default, a slower legitimate retry attempt
      // could trip this timeout even though nothing is actually wrong.
      const timeoutMs = type === 'OX' ? 70000 : type === 'BP' ? 75000 : X6ManagerService.DETECTION_TIMEOUT_MS;
      this.detectionTimeoutHandle = setTimeout(() => {
        console.warn(`[X6Manager] ${type} timed out after ${timeoutMs}ms with no result -- forcing stop`);
        this.stopDetectionInternal(type);
        this.setState({
          ...this.stateSubject.value,
          activeDetectionError: `${type} measurement timed out. Please check the device and try again.`,
          activeDetectionErrorType: 'sdk'
        });
      }, timeoutMs);
    };

    if (waitMs > 0) {
      console.log(`[X6Manager] Delaying start of ${type} by ${waitMs}ms (GATT collision / BLE notification-enable margin)`);
      this.pendingStartHandle = setTimeout(reallyStart, waitMs);
    } else {
      reallyStart();
    }
  }

  stopDetection(type: X6DetectionType): void {
    if (!this.manager) return;
    console.log('[X6Manager] Stopping detection:', type);
    this.clearDetectionTimeout();
    this.manager.stopDetection(Detection[type]);
    this.lastStopIssuedAt = Date.now();
    this.setState({
      ...this.stateSubject.value,
      activeDetection: undefined,
      activeDetectionError: undefined,
      activeDetectionErrorType: undefined
    });
  }

  /**
   * The Web/JS SDK exposes no public disconnect method at all (checked the
   * full API guide and the demo source -- unlike the Android SDK, which has
   * an explicit service.disConnect()). Previously the UI's Disconnect
   * button called nothing on this service at all -- it just changed screens,
   * leaving any active detection and the underlying GATT session running
   * silently in the background. Best available fix: stop whatever's active
   * and reset all session state, mirroring what Android's disconnect path
   * does before calling disConnect(). The underlying BLE link itself can't
   * be forced closed from here without an SDK method for it -- it stays
   * open until the device drops it or the tab closes.
   */
  disconnect(): void {
    if (!this.manager) return;
    console.log('[X6Manager] Disconnect requested by user');
    const activeDetection = this.stateSubject.value.activeDetection;
    if (activeDetection) {
      this.stopDetectionInternal(activeDetection);
    }
    this.clearDetectionTimeout();
    this.clearPendingStart();
    this.stopKeepAlive();
    this.invalidDataStreak = 0;
    this.invalidDataStreakStartedAt = 0;
    this.ecgFingerContactEstablished = false;
    this.oxFingerContactEstablished = false;
    this.oxRawSampleCount = 0;
    this.oxSessionActive = false;
    this.btSessionActive = false;
    this.bgSessionActive = false;
    this.bpSessionActive = false;
    this.batterySessionActive = false;
    this.setState({ ...INITIAL_STATE });
  }

  /**
   * Periodically reads battery (cheap, already-supported, harmless) while
   * connected and not otherwise mid-detection, purely to keep GATT traffic
   * flowing often enough that the device's own firmware never idles the
   * radio/MCU long enough to require a power-cycle to wake back up -- see
   * the KEEP_ALIVE_INTERVAL_MS field comment above for the full symptom
   * this works around. Skips a tick entirely (rather than interrupting)
   * whenever a real, user-initiated detection is already active.
   */
  private startKeepAlive(): void {
    this.stopKeepAlive();
    this.keepAliveInterval = setInterval(() => {
      if (this.stateSubject.value.status !== 'connected') { this.stopKeepAlive(); return; }
      if (this.stateSubject.value.activeDetection) return;
      console.log('[X6Manager] Keep-alive: reading battery to prevent the device/BLE link from idling out between patients');
      this.startDetection('BATTERY');
    }, X6ManagerService.KEEP_ALIVE_INTERVAL_MS);
  }

  private stopKeepAlive(): void {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = null;
    }
  }

  private clearDetectionTimeout(): void {
    if (this.detectionTimeoutHandle) {
      clearTimeout(this.detectionTimeoutHandle);
      this.detectionTimeoutHandle = null;
    }
  }

  private clearPendingStart(): void {
    if (this.pendingStartHandle) {
      clearTimeout(this.pendingStartHandle);
      this.pendingStartHandle = null;
    }
  }

  private setState(state: X6State): void {
    this.stateSubject.next(state);
    this.publishState(state);
  }
}
